import { when } from 'mobx'
import { type IResponseConversation } from 'entities/Conversation/api/types'
import { type ConversationListFilter } from 'entities/WebMcp/model/schemas'
import {
  serializeConversationSummary,
  type ConversationSummary,
} from 'entities/WebMcp/lib/serializers'

// isLoading covers the reload the filter change triggers; wait it out before reading rows.
// Matches the READY_TIMEOUT used by ContactsListSyncTools.
const READY_TIMEOUT = 8000
const MAX_RESULTS = 25

/**
 * The subset of ConversationListStore this tool drives. Declared structurally so the
 * helper is unit-testable with a plain mock and does not pull in the full page store.
 */
export interface ConversationListDriver {
  filtersStore: {
    // Each filter carries the server-provided UI label (e.g. pending -> "Needs response").
    getItem(key: string): { label: string } | undefined
    // Non-silent so the store's filter-change subscription fires and reloads the list.
    setFilter(key: string): void
  }
  loading: boolean
  hasMore: boolean
  listOrders: Array<{ origin: IResponseConversation }>
  conversationSearchStore: { hasSearchParams: boolean }
}

export type FilterConversationsListResult = {
  applied_filter: string
  applied_filter_label: string
  showing: number
  has_more: boolean
  results: ConversationSummary[]
}

export const FILTER_CONVERSATIONS_LIST_GUIDANCE =
  'The on-screen conversation list is now filtered to the applied_filter_label field in the data below. The user can see these results on screen. Confirm briefly with the count; do not repeat the rows as a table in chat.'

/**
 * Apply a list filter to the on-screen conversation list the same way the inbox's own
 * filter dropdown does: a non-silent `setFilter` fires the store's filter-change
 * subscription, which resets the list and refetches for the new filter. Waits for the
 * refetch to settle, then returns the visible rows.
 */
export const filterConversationsList = async (
  store: ConversationListDriver,
  filter: ConversationListFilter,
  options?: { timeout?: number }
): Promise<FilterConversationsListResult> => {
  const filterItem = store.filtersStore.getItem(filter)
  if (!filterItem) {
    throw new Error(`The "${filter}" filter is not available in the current inbox.`)
  }

  if (store.conversationSearchStore.hasSearchParams) {
    throw new Error('A conversation search is active. Clear the search before filtering the list.')
  }

  store.filtersStore.setFilter(filter)

  await when(() => !store.loading, { timeout: options?.timeout ?? READY_TIMEOUT })
  // The store flips `loading` false one microtask before the refreshed rows are applied
  // to the list (it is built for reactive consumers, not imperative reads). Yield a
  // macrotask so that apply cascade drains before we snapshot listOrders — otherwise we
  // read the empty post-reset list while the screen itself updates correctly.
  await new Promise((resolve) => setTimeout(resolve))

  // Describe the result with the UI label the user sees (e.g. "Needs response"), not the store key.
  const rows = store.listOrders.slice(0, MAX_RESULTS)
  return {
    applied_filter: filter,
    applied_filter_label: filterItem.label,
    showing: rows.length,
    has_more: store.hasMore,
    results: rows.map((c) => serializeConversationSummary(c.origin)),
  }
}
